import { defaultRuleEditorState, RuleEditorContextProvider, RulesList } from '@innovatm/js-rule-editor';
import { CircularProgress } from '@mui/material';
import React, { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Route, Routes, useNavigate } from 'react-router-dom';

import {
    GetGetRulesResponse,
    useQueryGetGetEnumNames,
    useQueryGetGetObjectSchemas,
    useQueryGetGetRuleConstants,
    useQueryGetGetRules,
} from '../../backend';
import { isResourceManagerRule } from '../../backend/utils';
import { useCurrentUserClient } from '../../contexts/OidcContext/OidcContext.selectors';
import { ROUTE_EDITOR } from '../../routes';
import { RoundButton } from '../Button/RoundButton/RoundButton';
import { RuleEditorEdit, RuleEditorNew } from '../RuleEditor/RuleEditor';
import { RuleEditorContainer } from '../RuleEditor/RuleEditorContainer';
import { RuleEditorPageHeader } from '../RuleEditor/RuleEditorPageHeader';
import { renderRow } from '../RuleEditor/RuleRowDisplay';
import { TestingEditor } from '../RuleEditor/TestingEditor/TestingEditor';
import { TestingEditorContainer } from '../RuleEditor/TestingEditor/TestingEditorContainer';
import {
    StyledRuleEditorBox,
    StyledRuleEditorContentBox,
    StyledRuleListContentBox,
    StyledRuleListTitleBox,
    StyledRulesListBox,
} from './RuleEditorPage.style';

export const RuleEditorPage = memo(function RuleEditorPage() {
    const client = useCurrentUserClient();
    const { data: rules } = useQueryGetGetRules(
        { context: client.airportIata },
        {
            placeholderData: { data: [] } as GetGetRulesResponse,
            select: response => {
                return {
                    ...response,
                    data: response.data.sort((a, b) => {
                        if (isResourceManagerRule(a) && isResourceManagerRule(b)) {
                            return (
                                a.resourceType.localeCompare(b.resourceType) ||
                                a.name.localeCompare(b.name, undefined, { numeric: true })
                            );
                        } else {
                            return a.name.localeCompare(b.name, undefined, { numeric: true });
                        }
                    }),
                };
            },
            notifyOnChangeProps: ['data', 'error'],
        },
    );
    const { data: schema } = useQueryGetGetObjectSchemas();
    const { data: enums } = useQueryGetGetEnumNames();
    const { data: constants } = useQueryGetGetRuleConstants();
    const navigate = useNavigate();

    if (!schema || !enums || !constants) {
        return (
            <StyledRuleEditorBox>
                <CircularProgress />
            </StyledRuleEditorBox>
        );
    }

    return (
        <RuleEditorContextProvider
            initialState={{ ...defaultRuleEditorState, ruleType: 'ResourceManagerRule', schema, enums, constants }}
        >
            <StyledRuleEditorBox>
                <RuleEditorPageHeader />

                <StyledRuleEditorContentBox>
                    <StyledRulesListBox>
                        <StyledRuleListTitleBox>
                            <FormattedMessage id={'ruleEditor.rulesList.title'} />
                        </StyledRuleListTitleBox>

                        <StyledRuleListContentBox>
                            <RulesList rules={rules || []} renderRow={renderRow} />
                        </StyledRuleListContentBox>
                        {!!rules?.length && (
                            <RoundButton
                                color="primary"
                                size="medium"
                                variant="outlined"
                                sx={{ width: '100%', marginTop: '20px' }}
                                onClick={() => navigate(`${ROUTE_EDITOR}/test`)}
                            >
                                <FormattedMessage id={'ruleEditor.testing.editor'} />
                            </RoundButton>
                        )}
                    </StyledRulesListBox>
                    <Routes>
                        <Route path="test" element={<TestingEditorContainer />}>
                            <Route path=":name" element={<TestingEditor rules={rules || []} />} />
                            <Route index element={<TestingEditor rules={rules || []} />} />
                        </Route>
                        <Route path=":type" element={<RuleEditorContainer />}>
                            <Route path=":name" element={<RuleEditorEdit rules={rules || []} />} />
                            <Route path=":name/duplicate" element={<RuleEditorNew rules={rules || []} />} />
                            <Route index element={<RuleEditorNew />} />
                        </Route>
                    </Routes>
                </StyledRuleEditorContentBox>
            </StyledRuleEditorBox>
        </RuleEditorContextProvider>
    );
});
